import { Stack } from "@mui/material";
import { API } from "aws-amplify";
import { format } from "date-fns";
import { useCreateApiCall } from "hooks/useCreateApiCall";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useAppDispatch } from "store/hooks";
import { showError } from "store/reducers/errorReducer";
import { FormSelectField } from "UI/FormSelectField";
import { fillMissingDays } from "utils/fillMissingDays";
import { fillMissingMonths } from "utils/fillMissingMonths";
import { fillMissingWeeks } from "utils/fillMissingWeeks";
import { fillMissingYears } from "utils/fillMissingYears";
import { AnalyticsRangeSelector } from "./AnalyticsRangeSelector";

const parameters = {
  Specialists: "Specialists",
  Practices: "Practices",
  Providers: "Providers",
  Senders: "Senders",
};

const defaultValues = {
  location: "*",
  parameter: parameters.Specialists,
  period: "Daily",
  start: "",
  end: "",
};

export const defaultNewFilters = {
  location: null,
  parameter: parameters.Specialists,
  period: "Daily",
  start: null,
  end: null,
};

export const LocationAnalyticsFilter = ({
  isLoading,
  setData,
  setLoading,
  setTotalData,
  setSubtotalData,
  setSubtotalNqrData,
  setItemName,
  setPeriod,
  setFilter,
  icon,
}) => {
  const dispatch = useAppDispatch();
  const { createApiCall } = useCreateApiCall();
  const [newFilters, setNewFilters] = useState(defaultNewFilters);

  const [locationsOptions, setLocationsOptions] = useState({});

  const {
    control,
    watch,
    formState: { isDirty },
    handleSubmit,
    setValue,
    getValues,
    reset,
  } = useForm({
    defaultValues,
  });

  const { location, parameter, period, start, end } = watch();

  const getPreparedData = () => {
    const data = getValues();

    const newData = { ...data };
    if (newData?.start !== "") {
      newData.start = format(new Date(newData.start), "yyyy-MM-dd");
    } else {
      newData.start = null;
    }
    if (newData?.end !== "") {
      newData.end = format(new Date(newData.end), "yyyy-MM-dd");
    } else {
      newData.end = null;
    }
    if (newData?.location === "*") {
      newData.location = null;
    }
    return newData;
  };

  const loadTotalData = createApiCall(async () => {
    const data = getPreparedData();
    const result = await API.post("restApiV2", "/analytics/location/total", {
      body: {
        ...data,
      },
    });

    if (result?.error) {
      console.error(result.error);
      dispatch(showError("Error loading total data"));
      setTotalData(null);
      return;
    }

    setTotalData(result);
  });

  const loadSubtotalNqrData = createApiCall(async () => {
    const data = getPreparedData();
    const result = await API.post(
      "restApiV2",
      "/analytics/location/subtotal/NQR",
      {
        body: {
          ...data,
        },
      }
    );

    if (result?.error) {
      console.error(result.error);
      dispatch(showError("Error loading subtotal data"));
      setSubtotalNqrData([]);
      return;
    }

    setSubtotalNqrData(result);
  });

  const loadSubtotalData = createApiCall(async () => {
    const data = getPreparedData();
    const result = await API.post("restApiV2", "/analytics/location/subtotal", {
      body: {
        ...data,
      },
    });

    if (result?.error) {
      console.error(result.error);
      dispatch(showError("Error loading subtotal data"));
      setSubtotalData([]);
      return;
    }

    setSubtotalData(result);
  });

  const loadClicksData = createApiCall(async () => {
    setLoading(true);
    const defaultData = getValues();
    const data = getPreparedData();
    const name = getItemName(data);
    setItemName(name);
    const newPeriod = data?.period;

    const result = await API.post(
      "restApiV2",
      "/analytics/location/graph/clicks",
      {
        body: {
          ...data,
        },
      }
    );

    if (result?.error) {
      console.error(result.error);
      dispatch(showError("Error loading data"));
      setData([]);
      setLoading(false);
      return;
    }

    const { period, start, end } = data;

    let augmentedData;
    switch (period) {
      case "Weekly":
        augmentedData = fillMissingWeeks(
          result,
          start?.slice(0, 10),
          end?.slice(0, 10)
        );
        break;
      case "Monthly":
        augmentedData = fillMissingMonths(
          result,
          start?.slice(0, 10),
          end?.slice(0, 10)
        );
        break;
      case "Yearly":
        augmentedData = fillMissingYears(
          result,
          start?.slice(0, 10),
          end?.slice(0, 10)
        );
        break;
      default:
        augmentedData = fillMissingDays(
          result,
          start?.slice(0, 10),
          end?.slice(0, 10)
        );
    }

    setData(augmentedData);
    setLoading(false);
    newPeriod && setPeriod(newPeriod);
    setTimeout(() => reset(defaultData), 300);
  });

  const getItemName = (data) => {
    const { parameter } = data;

    return parameters[parameter];
  };

  const onSubmit = (data) => {
    if (data?.start > data?.end) {
      dispatch(showError("Start date must be before end date"));
      return;
    }

    setFilter(getPreparedData());

    loadTotalData();
    loadSubtotalNqrData();
    loadSubtotalData();
    loadClicksData();
  };

  const isDateNotSelected =
    (start === "" && end !== "") || (start !== "" && end === "");

  useEffect(() => {
    setNewFilters(getPreparedData());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, parameter]);

  useEffect(() => {
    setNewFilters(getPreparedData());
    reset(getValues());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  useEffect(() => {
    setNewFilters(getPreparedData());
    const getData = createApiCall(async () => {
      setLoading(true);
      const result = await API.post("restApi", "/locations", {
        body: {
          action: "locationOptions",
        },
      });

      if (result?.error) {
        dispatch(showError("Error loading locations options"));
        setLocationsOptions({});
        setLoading(false);
        return;
      }

      setLocationsOptions(result ?? {});
      setLoading(false);
    });

    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => onSubmit(), 50);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newFilters]);

  return (
    <div
      className={`
        flex flex-row justify-between flex-wrap items-center gap-2
        rounded-2xl p-4 bg-white shadow-sm 
    `}
    >
      <div className="flex-1 flex flex-col sm:flex-row justify-start sm:justify-stretch sm:flex-wrap items-stretch gap-2 max-w-full">
        <Stack flex={1}>
          <FormSelectField
            name="location"
            control={control}
            options={locationsOptions}
            withNone
            noneLabel="All Locations"
            noneValue="*"
            disabled={isLoading}
          />
        </Stack>
        <div className="flex flex-row justify-center items-center ml-2">
          Group by:
        </div>
        <Stack flex={1}>
          <FormSelectField
            name="parameter"
            control={control}
            options={{
              Specialists: "Specialists",
              Practices: "Practices",
              Providers: "Providers",
              Senders: "Senders",
            }}
            disabled={isLoading}
          />
        </Stack>
      </div>
      <div className="flex flex-row justify-center items-center gap-2">
        <AnalyticsRangeSelector
          control={control}
          watch={watch}
          getValues={getValues}
          setValue={setValue}
          onSubmit={handleSubmit(onSubmit)}
        />
        {isDirty && !isDateNotSelected ? (
          <button
            disabled={!isDirty || isDateNotSelected}
            onClick={handleSubmit(onSubmit)}
            className="bg-black rounded-xl p-3 pl-5 pr-5 text-sm text-white"
          >
            View
          </button>
        ) : (
          <button className="bg-gray-500 rounded-xl p-3 pl-5 pr-5 text-sm text-white">
            View
          </button>
        )}
        {!isDirty && <>{icon}</>}
      </div>
    </div>
  );
};
